import type { AuthResponse } from '@/tygo/dto'
import { getQueryClient } from '@/integrations/tanstack-query/root-provider'
import { authQueryOptions } from '@/domains/auth/queries'

export type ApiError = {
  message: string
  code?: string
}

export class HttpError extends Error {
  status: number
  body?: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.status = status
    this.body = body
  }
}

let refreshPromise: Promise<AuthResponse> | null = null

export async function apiFetch<T>(
  path: string,
  options: RequestInit & { skipRefresh?: boolean } = {},
  _isRetry = false,
): Promise<T> {
  const { skipRefresh, ...fetchOptions } = options
  const res = await fetch(
    `${process.env.VITE_API_URL ?? import.meta.env.VITE_API_URL}${path}`,
    {
      ...fetchOptions,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(fetchOptions.headers ?? {}),
      },
    },
  ).catch((err) => {
    throw err
  })

  const text = await res.text()
  const maybeJson = text ? safeJsonParse(text) : undefined

  if (res.status === 401 && !_isRetry && !skipRefresh) {
    // If a refresh is already in progress, wait for it
    if (refreshPromise) {
      try {
        const authData = await refreshPromise
        return apiFetch<T>(
          path,
          replaceAuthHeader(options, authData.access_token),
          true,
        )
      } catch {
        throw new HttpError(401, 'Session expired')
      }
    }

    // Start a new refresh
    refreshPromise = apiFetch<AuthResponse>(
      '/api/v1/auth/refresh',
      { method: 'GET' },
      true,
    )
      .then((authData) => {
        getQueryClient().setQueryData(authQueryOptions('').queryKey, authData)
        return authData
      })
      .finally(() => {
        refreshPromise = null
      })

    try {
      const authData = await refreshPromise
      return apiFetch<T>(
        path,
        replaceAuthHeader(options, authData.access_token),
        true,
      )
    } catch {
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
      throw new HttpError(401, 'Session expired')
    }
  }

  if (!res.ok) {
    const msg =
      (maybeJson && (maybeJson as any).message) ||
      text ||
      `Request failed with status ${res.status}`
    throw new HttpError(res.status, msg, maybeJson ?? text)
  }

  return maybeJson as T
}

function safeJsonParse(text: string): unknown | undefined {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

function replaceAuthHeader(
  options: RequestInit,
  accessToken: string,
): RequestInit {
  const headers = options.headers as Record<string, string> | undefined
  if (!headers?.Authorization) return options
  return {
    ...options,
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`,
    },
  }
}
